class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.searchDropdown = this.querySelector('[data-search-dropdown]');
    this.idlePanel = this.querySelector('[data-search-idle-panel]');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.recentStorageKey = 'ack-recent-searches';
    this.maxRecentSearches = 9;
    this.predictiveEnabled = this.getAttribute('data-predictive-enabled') !== 'false';
    this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
    this.onViewportChange = this.onViewportChange.bind(this);
    this.preserveOpen = false;

    this.setupEventListeners();
    this.initIdlePanel();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('click', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('pointerdown', this.onPointerDown.bind(this));
    document.addEventListener('pointerdown', this.onDocumentPointerDown);
    window.addEventListener('resize', this.onViewportChange);
    window.visualViewport?.addEventListener('resize', this.onViewportChange);
  }

  initIdlePanel() {
    if (!this.idlePanel) return;

    this.idlePanel.querySelector('[data-search-idle-close]')?.addEventListener('click', (event) => {
      event.preventDefault();
      this.close();
      this.input.blur();
    });

    this.idlePanel.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-search-term]');
      if (chip) this.saveRecentSearch(chip.dataset.searchTerm);
    });

    this.predictiveSearchResults?.addEventListener('click', (event) => {
      if (event.target.closest('a[href]')) this.saveRecentSearch(this.getQuery());
    });

    this.relocateQuickAddModals();
    this.captureRecentFromUrl();
    this.renderRecentSearches();
  }

  relocateQuickAddModals() {
    this.idlePanel?.querySelectorAll('quick-add-modal').forEach((modal) => {
      if (modal.parentElement !== document.body) {
        document.body.appendChild(modal);
      }
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.openIdle();
      return;
    }

    this.hideIdle();

    if (!this.predictiveEnabled) {
      this.closeResults();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const query = this.getQuery();
    if (query.length) this.saveRecentSearch(query);

    if (!query.length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
      this.openIdle();
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.openIdle();
      return;
    }

    if (!this.predictiveEnabled) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.hideIdle();
      this.open();
    } else {
      this.hideIdle();
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.preserveOpen) return;
      if (this.contains(document.activeElement)) return;
      if (document.activeElement?.closest('quick-add-modal')) return;
      this.close();
    });
  }

  onPointerDown(event) {
    const dropdown = event.target.closest('[data-search-dropdown]');
    if (!dropdown) return;

    if (event.target.closest('[data-search-recommended-slider]')) {
      this.preserveOpen = true;
      return;
    }

    if (event.target.closest('a, button, input, modal-opener, .quick_view')) {
      this.preserveOpen = true;
      return;
    }

    const scrollEl = event.target.closest('.header-custom__search-results') || dropdown;
    if (scrollEl.scrollHeight > scrollEl.clientHeight) {
      const bounds = scrollEl.getBoundingClientRect();
      if (event.clientX >= bounds.left + scrollEl.clientWidth) return;
    }

    event.preventDefault();
  }

  onViewportChange() {
    if (!this.isOpen) return;
    this.updateDropdownPosition();
  }

  onDocumentPointerDown(event) {
    if (!this.isOpen) return;
    if (this.contains(event.target)) return;
    if (event.target.closest('quick-add-modal')) return;
    this.preserveOpen = false;
    this.close();
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.openIdle();
      event.preventDefault();
      return;
    }

    this.hideIdle();
    if (this.predictiveEnabled) this.setAttribute('loading', true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }

    if (event.code === 'Escape') {
      this.close();
      this.input.blur();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open') || this.getAttribute('idle') === 'true') return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);

        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          searchDeferred?.reject(error);
          return;
        }
        searchDeferred?.reject(error);
        this.close();
        throw error;
      });
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
    this.open();
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.hideIdle();

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const liveRegionText = this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent;
    if (liveRegionText) this.setLiveRegionText(liveRegionText);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  hasIdleContent() {
    if (!this.idlePanel) return false;

    return Boolean(
      this.idlePanel.querySelector('.search-idle-section:not([hidden]), .search-idle-recommended')
    );
  }

  openIdle() {
    this.removeAttribute('loading');
    this.removeAttribute('results');
    this.abortController.abort();
    this.abortController = new AbortController();

    this.renderRecentSearches();

    if (!this.hasIdleContent()) {
      this.close();
      return;
    }

    this.setAttribute('idle', true);
    this.idlePanel.hidden = false;
    this.open();
    requestAnimationFrame(() => {
      this.initRecommendedCarousel();
      requestAnimationFrame(() => this.recommendedSwiper?.update());
    });
  }

  hideIdle() {
    this.removeAttribute('idle');
    if (this.idlePanel) this.idlePanel.hidden = true;
  }

  captureRecentFromUrl() {
    try {
      const url = new URL(window.location.href);
      const query = (url.searchParams.get('q') || '').trim();
      if (!query) return;

      const searchPath = (this.idlePanel?.dataset.searchUrl || '/search').replace(/\/$/, '');
      const currentPath = url.pathname.replace(/\/$/, '');
      if (currentPath !== searchPath && !currentPath.endsWith('/search')) return;

      this.saveRecentSearch(query);
    } catch (error) {
      // Ignore invalid URLs or blocked storage.
    }
  }

  getRecentSearches() {
    try {
      const stored = JSON.parse(localStorage.getItem(this.recentStorageKey));
      return Array.isArray(stored) ? stored.filter(Boolean) : [];
    } catch (error) {
      return [];
    }
  }

  saveRecentSearch(term) {
    const value = (term || '').trim();
    if (!value) return;

    const recents = this.getRecentSearches().filter(
      (item) => item.toLowerCase() !== value.toLowerCase()
    );
    recents.unshift(value);
    localStorage.setItem(this.recentStorageKey, JSON.stringify(recents.slice(0, this.maxRecentSearches)));
  }

  renderRecentSearches() {
    if (!this.idlePanel) return;

    const section = this.idlePanel.querySelector('[data-search-recent-section]');
    const list = this.idlePanel.querySelector('[data-search-recent-list]');
    const template = this.idlePanel.querySelector('[data-search-chip-template]');
    if (!section || !list || !template) return;

    const recents = this.getRecentSearches();
    const chipTemplate = template.content.querySelector('.search-idle-chip');
    list.innerHTML = '';

    if (!recents.length || !chipTemplate) {
      section.hidden = true;
      return;
    }

    const searchUrl = this.idlePanel.dataset.searchUrl || '/search';

    recents.forEach((term) => {
      const chip = chipTemplate.cloneNode(true);
      chip.dataset.searchTerm = term;
      chip.href = `${searchUrl}?q=${encodeURIComponent(term)}&type=product`;
      chip.querySelector('.search-idle-chip__label').textContent = term;
      list.appendChild(chip);
    });

    section.hidden = false;
  }

  initRecommendedCarousel() {
    const slider = this.idlePanel?.querySelector('[data-search-recommended-slider]');
    if (!slider || !slider.querySelector('.product_slide')) return;

    if (!window.Swiper) {
      window.setTimeout(() => this.initRecommendedCarousel(), 50);
      return;
    }

    if (this.recommendedSwiper) {
      this.recommendedSwiper.update();
      this.recommendedSwiper.pagination?.render();
      this.recommendedSwiper.pagination?.update();
      return;
    }

    this.relocateQuickAddModals();

    this.recommendedSwiper = new Swiper(slider, {
      slidesPerView: 2.7,
      spaceBetween: 12,
      slidesOffsetBefore: 24,
      slidesOffsetAfter: 24,
      nested: true,
      grabCursor: true,
      simulateTouch: true,
      allowTouchMove: true,
      observer: true,
      observeParents: true,
      watchOverflow: true,
      preventClicks: true,
      preventClicksPropagation: true,
      pagination: {
        el: slider.querySelector('.swiper-pagination'),
        clickable: true,
      },
      breakpoints: {
        0: {
          slidesPerView: 1.4,
          spaceBetween: 12,
          slidesOffsetBefore: 20,
          slidesOffsetAfter: 20,
        },
        480: {
          slidesPerView: 2.2,
          spaceBetween: 12,
          slidesOffsetBefore: 24,
          slidesOffsetAfter: 24,
        },
        600: {
          slidesPerView: 2.7,
          spaceBetween: 12,
          slidesOffsetBefore: 24,
          slidesOffsetAfter: 24,
        },
      },
    });

    slider.addEventListener(
      'click',
      (event) => {
        if (this.recommendedSwiper && this.recommendedSwiper.allowClick === false) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        const opener = event.target.closest('modal-opener');
        if (opener) {
          event.preventDefault();
          event.stopPropagation();
          const button =
            opener.querySelector('[data-product-url]') || opener.querySelector('.quick_view_inner') || opener;
          const modal = document.querySelector(opener.getAttribute('data-modal'));
          if (modal && typeof modal.show === 'function') modal.show(button);
          return;
        }

        const link = event.target.closest('a[href]');
        if (!link) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        event.stopPropagation();
        window.location.assign(link.href);
      },
      true
    );
  }

  updateDropdownPosition() {
    if (!this.searchDropdown) return;

    const form = this.input?.form || this;
    const offset = Math.max(0, Math.round(form.getBoundingClientRect().bottom + 8));
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const available = Math.max(160, Math.round(viewportHeight - offset - 8));

    this.searchDropdown.style.setProperty('--search-dropdown-offset', `${offset}px`);
    this.searchDropdown.style.maxHeight = `${Math.min(420, available)}px`;
    this.searchDropdown.style.height = 'auto';
  }

  open() {
    const dropdown = this.searchDropdown || this.predictiveSearchResults;
    if (this.searchDropdown) {
      this.searchDropdown.setAttribute('data-lenis-prevent', '');
      this.predictiveSearchResults?.setAttribute('data-lenis-prevent', '');
      this.updateDropdownPosition();
    } else if (dropdown) {
      dropdown.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    }
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    window.lenis?.stop();
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    window.lenis?.start();
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.removeAttribute('idle');
    if (this.idlePanel) this.idlePanel.hidden = true;
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults?.removeAttribute('style');
    this.searchDropdown?.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
